import { useNavigate } from "react-router";
import { Search, ChevronDown } from "lucide-react";
import { t } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";
import { formatCurrency } from "@/lib/utils/format";
import type { Contribution, ContributionStatus } from "@/types/contribution";

const TYPE_ICONS: Record<string, string> = {
  walk_initiative: "directions_walk",
  juthoor_initiative: "park",
};

const TYPE_LABEL_KEYS: Record<string, "typeWalk" | "typeJuthoor"> = {
  walk_initiative: "typeWalk",
  juthoor_initiative: "typeJuthoor",
};

interface MyContributionsTableProps {
  contributions: Contribution[];
  onSearch: (s: string) => void;
  onStatusFilter: (s: ContributionStatus | undefined) => void;
  onShowMore?: () => void;
  hasMore?: boolean;
  loading: boolean;
}

function ContributionStatusBadge({ status }: { status: ContributionStatus }) {
  const { locale } = useAppContext();
  const styles: Record<ContributionStatus, string> = {
    active: "bg-success-light text-success-dark dark:bg-success/40 dark:text-success",
    completed: "bg-info-light text-info-dark dark:bg-info/40 dark:text-info",
  };
  const labels: Record<ContributionStatus, string> = {
    active: t(locale, "active"),
    completed: t(locale, "completed"),
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

export function MyContributionsTable({
  contributions,
  onSearch,
  onStatusFilter,
  onShowMore,
  hasMore,
  loading,
}: MyContributionsTableProps) {
  const { locale } = useAppContext();
  const navigate = useNavigate();

  return (
    <section className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold">{t(locale, "myContributions")}</h3>
          <p className="text-sm text-foreground-secondary mt-1">
            {t(locale, "trackContributions")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search
              className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground w-[18px] h-[18px] pointer-events-none"
              strokeWidth={1.8}
              aria-hidden="true"
            />
            <input
              type="text"
              className="pe-10 ps-4 py-2 bg-muted border-none rounded-lg text-sm focus:ring-2 focus:ring-primary/20 w-full md:w-64"
              placeholder={t(locale, "searchByName")}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
          <div className="relative">
            <select
              className="bg-muted border-none rounded-lg text-sm py-2 ps-4 pe-10 focus:ring-2 focus:ring-primary/20 appearance-none bg-none"
              onChange={(e) =>
                onStatusFilter(
                  (e.target.value as ContributionStatus) || undefined
                )
              }
              defaultValue=""
            >
              <option value="">{t(locale, "selectStatusFilter")}</option>
              <option value="active">{t(locale, "active")}</option>
              <option value="completed">{t(locale, "completed")}</option>
            </select>
            <ChevronDown
              className="absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400 w-[18px] h-[18px]"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-start border-collapse">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "type")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "title")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "status")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "creationDate")}
              </th>
              <th className="px-6 py-4 text-xs font-bold text-foreground-secondary uppercase tracking-wider">
                {t(locale, "fundedBudget")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-20 text-center">
                  <span className="material-icons-outlined text-4xl text-primary animate-spin">
                    refresh
                  </span>
                </td>
              </tr>
            ) : contributions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-20 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-4">
                      <span className="material-icons-outlined text-4xl text-muted-foreground">
                        history
                      </span>
                    </div>
                    <p className="text-foreground-secondary font-medium">
                      {t(locale, "noContributions")}
                    </p>
                    <p className="text-muted-foreground text-sm mt-1">
                      {t(locale, "noContributionsDesc")}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              contributions.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => c.initiative_id && navigate(`/dashboard/initiatives/${c.initiative_id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600">
                        <span className="material-icons-outlined text-sm">
                          {TYPE_ICONS[c.initiative?.type ?? ""] ?? "eco"}
                        </span>
                      </div>
                      <span className="text-sm font-medium">
                        {c.initiative?.type ? t(locale, TYPE_LABEL_KEYS[c.initiative.type] ?? "typeWalk") : "-"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold">{c.initiative?.title ?? "-"}</td>
                  <td className="px-6 py-4">
                    <ContributionStatusBadge status={c.status} />
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground-secondary">
                    {c.created_at}
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-primary">
                    {formatCurrency(c.budget, locale)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Show more */}
      {hasMore && (
        <div className="p-4 border-t border-border bg-muted/50 text-center">
          <button
            type="button"
            onClick={onShowMore}
            className="text-sm text-primary font-semibold hover:underline"
          >
            {t(locale, "showMore")}
          </button>
        </div>
      )}
    </section>
  );
}
